import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { requestTransfer, findEquipmentBySerial } from '../services/transferService';
import './RequestTransfer.css';

function RequestTransfer({ user, onBack }) {
  const [step, setStep] = useState('search'); // search | form | success

  const [serialInput, setSerialInput] = useState('');
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [foundEquipment, setFoundEquipment] = useState(null);

  const [outlets, setOutlets] = useState([]);
  const [outletSearch, setOutletSearch] = useState('');
  const [showOutletDropdown, setShowOutletDropdown] = useState(false);
  const [selectedOutlet, setSelectedOutlet] = useState(null);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');

  const [myTransfers, setMyTransfers] = useState([]);

  // Load outlets once
  useEffect(() => {
    const loadOutlets = async () => {
      try {
        const snap = await getDocs(query(collection(db, 'outlets'), where('status', '==', 'active')));
        setOutlets(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch (err) {
        console.error('Error loading outlets:', err);
      }
    };
    loadOutlets();
  }, []);

  // Live list of transfers this user has initiated
  useEffect(() => {
    const q = query(collection(db, 'transfers'), where('initiatedBy', '==', user.id));
    const unsub = onSnapshot(q, (snap) => {
      const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      list.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
      setMyTransfers(list);
    });
    return () => unsub();
  }, [user.id]);

  const handleSearch = async () => {
    if (!serialInput.trim()) {
      setSearchError('Enter a serial number to search.');
      return;
    }
    setSearching(true);
    setSearchError('');
    try {
      const equipment = await findEquipmentBySerial(serialInput);
      if (!equipment) {
        setSearchError('No equipment found with that serial number.');
        setSearching(false);
        return;
      }
      setFoundEquipment(equipment);
      setStep('form');
    } catch (err) {
      console.error('Search error:', err);
      setSearchError('Something went wrong searching. Please try again.');
    } finally {
      setSearching(false);
    }
  };

  const filteredOutlets = outlets.filter(o => {
    const name = typeof o.outletName === 'string' ? o.outletName.toLowerCase() : '';
    return name.includes(outletSearch.toLowerCase()) && o.id !== foundEquipment?.outletId;
  });

  const handleOutletSelect = (outlet) => {
    setSelectedOutlet(outlet);
    setOutletSearch(outlet.outletName);
    setShowOutletDropdown(false);
    setFormError('');
  };

  const handleSubmit = async () => {
    if (!selectedOutlet) {
      setFormError('Please select a destination outlet.');
      return;
    }
    if (!reason.trim()) {
      setFormError('Please provide a reason for this transfer.');
      return;
    }

    setSubmitting(true);
    setFormError('');

    try {
      await requestTransfer({
        equipment: foundEquipment,
        toOutlet: selectedOutlet,
        reason,
        user
      });
      setStep('success');
    } catch (err) {
      console.error('Error requesting transfer:', err);
      setFormError('Something went wrong submitting this request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setStep('search');
    setSerialInput('');
    setFoundEquipment(null);
    setOutletSearch('');
    setSelectedOutlet(null);
    setReason('');
    setSearchError('');
    setFormError('');
  };

  // status → { icon, label, class modifier } — colors now live in CSS
  // (status-pending / status-confirmed / status-rejected / status-completed)
  const statusInfo = (status) => {
    const map = {
      pending:   { icon: '⏳', label: 'Pending Review' },
      confirmed: { icon: '✅', label: 'Approved' },
      rejected:  { icon: '🚫', label: 'Rejected' },
      completed: { icon: '📦', label: 'Completed' }
    };
    return { cls: `status-${status}`, ...(map[status] || map.pending) };
  };

  const StatusBadge = ({ status }) => {
    const s = statusInfo(status);
    return (
      <span className={`rqt-status-badge ${s.cls}`}>
        {s.icon} {s.label}
      </span>
    );
  };

  return (
    <div className="rqt-page">
      <header className="rqt-header">
        <button onClick={onBack} className="back-button">← Back</button>
        <div className="rqt-header-title">
          <span className="rqt-header-icon">🔄</span>
          <div>
            <h1 className="rqt-header-h1">Equipment Transfer</h1>
            <p className="rqt-header-sub">Move equipment between outlets</p>
          </div>
        </div>
      </header>

      <div className="rqt-form-wrapper">

        {step === 'search' && (
          <div className="rqt-section">
            <label className="rqt-label">Equipment Serial Number</label>
            <div className="rqt-row">
              <input
                className="rqt-input"
                type="text"
                placeholder="e.g. KEG-4821"
                value={serialInput}
                onChange={(e) => { setSerialInput(e.target.value); setSearchError(''); }}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              />
              <button className="rqt-search-btn" onClick={handleSearch} disabled={searching}>
                {searching ? '...' : 'Find'}
              </button>
            </div>
            {searchError && <p className="rqt-error-text">⚠️ {searchError}</p>}
          </div>
        )}

        {step === 'form' && foundEquipment && (
          <>
            <div className="rqt-found-card">
              <div className="rqt-found-row">
                <span className="rqt-found-label">Equipment</span>
                <span className="rqt-found-value">
                  {foundEquipment.equipmentType === 'keg_pump' ? '☕ Keg Pump' : '🍺 Draft Machine'} — {foundEquipment.serialNumber}
                </span>
              </div>
              <div className="rqt-found-row">
                <span className="rqt-found-label">Currently at</span>
                <span className="rqt-found-value">🏪 {foundEquipment.outletName}</span>
              </div>
            </div>

            <div className="rqt-section">
              <label className="rqt-label">Transfer To</label>
              <input
                className="rqt-input"
                type="text"
                placeholder="Search destination outlet..."
                value={outletSearch}
                onChange={(e) => { setOutletSearch(e.target.value); setShowOutletDropdown(true); setSelectedOutlet(null); }}
                onFocus={() => setShowOutletDropdown(true)}
              />
              {showOutletDropdown && outletSearch && (
                <div className="rqt-dropdown">
                  {filteredOutlets.length === 0 ? (
                    <div className="rqt-dropdown-empty">No matching outlets</div>
                  ) : filteredOutlets.map(outlet => (
                    <div key={outlet.id} className="rqt-dropdown-item" onClick={() => handleOutletSelect(outlet)}>
                      🏪 {outlet.outletName}
                    </div>
                  ))}
                </div>
              )}
              {selectedOutlet && (
                <p className="rqt-selected-text">✅ {selectedOutlet.outletName}</p>
              )}
            </div>

            <div className="rqt-section">
              <label className="rqt-label">Reason for Transfer</label>
              <textarea
                className="rqt-textarea"
                placeholder="Why is this equipment being moved? (e.g. outlet closure, reassignment, repair relocation)"
                value={reason}
                onChange={(e) => { setReason(e.target.value); setFormError(''); }}
                rows={4}
              />
            </div>

            {formError && <p className="rqt-error-text">⚠️ {formError}</p>}

            <div className="rqt-row">
              <button className="rqt-secondary-btn" onClick={resetForm} disabled={submitting}>Cancel</button>
              <button className="rqt-submit-btn" onClick={handleSubmit} disabled={submitting}>
                {submitting ? 'Submitting...' : '🔄 Submit Transfer Request'}
              </button>
            </div>
          </>
        )}

        {step === 'success' && (
          <div className="rqt-success-card">
            <div className="rqt-success-icon">✅</div>
            <h2 className="rqt-success-title">Request Submitted</h2>
            <p className="rqt-success-text">
              Your transfer request is waiting for supervisor approval.
            </p>
            <button className="rqt-submit-btn" onClick={resetForm}>Request Another Transfer</button>
          </div>
        )}

        {/* My Transfer Requests */}
        <div className="rqt-history-section">
          <h3 className="rqt-section-title">My Transfer Requests</h3>
          {myTransfers.length === 0 ? (
            <p className="rqt-empty-text">No transfer requests yet.</p>
          ) : (
            <div className="rqt-history-list">
              {myTransfers.map(t => (
                <div key={t.id} className="rqt-history-card">
                  <div className="rqt-history-top">
                    <span className="rqt-history-serial">{t.equipmentSerial}</span>
                    <StatusBadge status={t.status} />
                  </div>
                  <div className="rqt-history-meta">
                    {t.fromOutletName} → {t.toOutletName}
                  </div>
                  {t.status === 'rejected' && t.rejectionReason && (
                    <div className="rqt-rejection-note">Reason: {t.rejectionReason}</div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default RequestTransfer;
